import { useAppSelector } from "app/hooks";
import { StatTable } from "components/StatsTable/StatTable";
import { StatTableColumn } from "components/StatsTable/StatTableColumn";
import { Match } from "model/Match/Match";
import { getRosterId } from "model/Roster/Roster";
import { selectRosterDisplayName, selectRosterOrgId } from "selectors/Roster";
import { nonUndefined } from "Utility";

const PAGINATION_AMOUNT = 12;

interface Props {
  match: Match;
  tournamentId: string;
}

type TeamTableStats = {
  orgId: string | undefined;
  key: string;
  displayName: string;
  position: number | undefined;
  points: number | undefined;
};

export const MatchViewerTabledTeams = ({ match, tournamentId }: Props) => {
  const stats: TeamTableStats[] = useAppSelector((state) =>
    nonUndefined(
      match.teams.rosters.map((r, rosterIdx) => {
        const rosterId = getRosterId(r);
        if (!rosterId) {
          return undefined;
        }

        const orgId = selectRosterOrgId(state, rosterId, tournamentId);
        const displayName =
          selectRosterDisplayName(state, rosterId, tournamentId) ??
          "Unknown Team";
        return {
          key: `${rosterIdx}:${rosterId}-${displayName}`,
          orgId,
          displayName,
          position: !!match.finalPositions
            ? match.finalPositions[rosterId]
            : undefined,
          points: !!match.scores ? match.scores[rosterId] : undefined,
        };
      })
    )
  );

  return (
    <StatTable
      stats={stats}
      columns={columns}
      keyGetter={(arg) => arg.key}
      paginationAmount={PAGINATION_AMOUNT}
      initialSortColumnIdx={match.isComplete ? 0 : 2}
      initialSortOrder={match.isComplete ? "ASC" : "DESC"}
      thinner={true}
      searchFunction={(arg, searchText) =>
        arg.displayName?.toLowerCase()?.includes(searchText.toLowerCase()) ??
        false
      }
      searchPlaceholder="Search by driver name"
    />
  );
};

const columns: StatTableColumn<TeamTableStats>[] = [
  {
    type: "Position",
    positionGetter: (arg) => arg.position,
  },
  {
    type: "OrgLogoName",
    nameGetter: (arg) => arg.displayName,
    orgIdGetter: (arg) => arg.orgId,
  },
  {
    type: "Number",
    valueGetter: (arg) => arg.points,
    fractionDigits: 0,
  },
];
